/**
 * The colours the module wears, supplied by the host app — D7 of the design-update plan
 * (Roman, 2026-09-28), and the file that keeps D6 of the What's New plan true.
 *
 * ## Why the module stopped choosing its own
 *
 * The classes below are Tailwind names, and a Tailwind name does not mean the same thing in two
 * apps. In `customer360-app` `blue-*` **is** the Dolder green (`web/tailwind.config.js` remaps
 * the whole scale), and there is an `info-*` scale for "neither won nor lost". In `pure-app`
 * `blue-*` is a real blue and there is **no `info` scale at all** — so `bg-info-50` on the
 * "Improved" chip rendered there as no background whatsoever, and `NOTICE_STYLES.info` rendered
 * here in the brand green, which design.md §2 reserves for "won". Two apps, two different
 * wrong answers, from one hard-coded map.
 *
 * Hard-coding either app's answer breaks the other, and asking the module to detect which app
 * it is in would be the module knowing about its host — exactly what D6 forbids. So the host
 * passes them in, and the module has a set of defaults that hold in a **stock** Tailwind app:
 * `green` for new, `blue` for informational, `amber` for "you must act", `red` for "stop". An
 * app that copies this folder and passes nothing gets a module that looks right; an app whose
 * palette says something else passes its own.
 *
 * `overlay` is in here for the same reason and not because it is a colour: the panel and the
 * dialog need *something* to lift them off a scrim (design.md §4), this app has a token for it
 * (`shadow-overlay`) and a stock Tailwind app has `shadow-xl`. DU3 left `shadow-xl` in the
 * module for that reason and said it would travel with this prop.
 */

import type { NoticeSeverity, WhatsNewKind } from './types';

/** The three classes a notice needs: its band, its icon, and its dismiss button. */
export interface NoticeTone {
    bar: string;
    icon: string;
    button: string;
}

export interface WhatsNewTones {
    /** The chip on an entry card, per kind. Ground, ink and border in one string. */
    kind: Record<WhatsNewKind, string>;
    /** The notice bar and the notice card, per severity. */
    notice: Record<NoticeSeverity, NoticeTone>;
    /** What lifts the panel and the blocking dialog off the page. */
    overlay: string;
}

/**
 * What the module looks like in a stock Tailwind app.
 *
 * Amber is deliberately absent from `kind`: a fixed bug is news, not a task, so `fixed` takes a
 * neutral and `improved` the informational blue. That reasoning is the module's and travels
 * with it; only the class names are the host's business.
 */
export const DEFAULT_TONES: WhatsNewTones = {
    kind: {
        new: 'bg-green-50 text-green-700 border-green-200',
        improved: 'bg-blue-50 text-blue-700 border-blue-200',
        fixed: 'bg-gray-100 text-gray-600 border-gray-200',
    },
    notice: {
        info: {
            bar: 'border-blue-200 bg-blue-50 text-blue-900',
            icon: 'text-blue-600',
            button: 'text-blue-700 hover:bg-blue-100',
        },
        warning: {
            bar: 'border-amber-200 bg-amber-50 text-amber-900',
            icon: 'text-amber-600',
            button: 'text-amber-800 hover:bg-amber-100',
        },
        critical: {
            bar: 'border-red-200 bg-red-50 text-red-900',
            icon: 'text-red-600',
            button: 'text-red-800 hover:bg-red-100',
        },
    },
    overlay: 'shadow-xl',
};

/**
 * The host's tones over the defaults, one level at a time.
 *
 * Shallow per group rather than shallow overall: an app that wants to move only the `info`
 * notice onto its own scale — which is precisely this app's case — would otherwise have to
 * restate `warning` and `critical` to keep them, and a restated copy is a copy that drifts.
 */
export function resolveTones(supplied?: DeepPartialTones): WhatsNewTones {
    if (!supplied) return DEFAULT_TONES;
    return {
        kind: { ...DEFAULT_TONES.kind, ...supplied.kind },
        notice: {
            info: { ...DEFAULT_TONES.notice.info, ...supplied.notice?.info },
            warning: { ...DEFAULT_TONES.notice.warning, ...supplied.notice?.warning },
            critical: { ...DEFAULT_TONES.notice.critical, ...supplied.notice?.critical },
        },
        overlay: supplied.overlay ?? DEFAULT_TONES.overlay,
    };
}

/** What a host may pass: any subset, at any of the three levels. */
export interface DeepPartialTones {
    kind?: Partial<Record<WhatsNewKind, string>>;
    notice?: Partial<Record<NoticeSeverity, Partial<NoticeTone>>>;
    overlay?: string;
}
